import { useMemo } from 'react'
import { useWatch, type Control, type FieldValues } from 'react-hook-form'
import { useAutoSave } from '@app/util/hooks/useAutoSave'

/**
 * Debounced autosave from current react-hook-form values. `watchDeps` should list
 * the watched fields so the autosave payload is referentially stable when
 * unchanged. `save` performs the actual mutation (e.g. a `client.call`).
 */
export const useFormWatchAutosave = <TFieldValues extends FieldValues>({
  control,
  toPayload,
  watchDeps,
  save,
}: {
  control: Control<TFieldValues>
  toPayload: (values: TFieldValues) => Record<string, unknown>
  watchDeps: (values: TFieldValues) => readonly unknown[]
  save: (value: Record<string, unknown>) => Promise<unknown>
}): void => {
  const values = useWatch({ control }) as TFieldValues

  const payload = useMemo(
    () => toPayload(values),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watchDeps(values) expands to field-level deps
    watchDeps(values),
  )

  useAutoSave(payload, save)
}
